import React, { Suspense } from "react";
import ProtectedRoute from "ProtectedRoute";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";

// Energy Management vertical routes. Now app-mode-gated (appMode:"energy")
// so they flow through the system switcher in DefaultSystemPicker and only
// surface inside the EnergyPortalShell sidebar — same pattern LCP + AFMS use.
const EnergyOverview = React.lazy(() => import("energy/pages/Overview"));
const EnergyControlCenter = React.lazy(() => import("energy/pages/ControlCenter"));
// Settings sub-pages — thin wrappers around the AFMS equivalents so the
// chrome (DashboardLayout/Navbar + Card frame) matches the Energy shell
// while the underlying super-admin endpoints handle org scoping.
const EnergySettingsOrganization = React.lazy(() => import("energy/pages/Settings/Organization"));
const EnergySettingsUsers = React.lazy(() => import("energy/pages/Settings/Users"));
const EnergySettingsAssets = React.lazy(() => import("energy/pages/Settings/Assets"));
const EnergySettingsDevices = React.lazy(() => import("energy/pages/Settings/Devices"));
const EnergyAssetEdit = React.lazy(() => import("energy/pages/AssetEdit"));
// My Inbox — the shared notifications-api inbox, the same screen the
// Environmental portal mounts at /afms/inbox. Rows are scoped to the caller by
// the server, so this surfaces one inbox in a second portal rather than an
// Energy-specific feed.
const EnergyInbox = React.lazy(() => import("energy/pages/Inbox"));

// Enterprise (Manage my Clients / Manage my Vendors) — same shared pages
// the AFMS + LCP portals mount. Sidebar gating (requiresEnterpriseOwner)
// keeps these hidden unless the caller is the OWNER of an enterprise org.
const EnterpriseManageMyClients = React.lazy(() => import("pages/Enterprise/ManageMyClients"));
const EnterpriseManageMyVendors = React.lazy(() => import("pages/Enterprise/ManageMyVendors"));

const wrap = (Component) => (
  <ProtectedRoute
    component={
      <Suspense fallback={null}>
        <Component />
      </Suspense>
    }
  />
);

const energyRoutes = [
  {
    // Control Center — landing page for the Energy portal. Blank in v1;
    // concrete dashboards come with Plan K Phase 3 stage 1.
    type: "collapse",
    name: "Energy Control Center",
    key: "energy-control-center",
    icon: <BoltOutlinedIcon />,
    route: "/energy/control-center",
    appMode: "energy",
    component: wrap(EnergyControlCenter),
    noCollapse: true,
  },
  {
    // Existing Overview route — retagged to app-mode gating for
    // consistency. Kept around so any deep links still resolve while
    // Energy concrete dashboards land.
    type: "hidden",
    name: "Energy Overview",
    key: "energy-overview",
    icon: <BoltOutlinedIcon />,
    route: "/energy/overview",
    appMode: "energy",
    component: wrap(EnergyOverview),
    noCollapse: true,
  },
  {
    // Hidden here because the sidebar entry is declared in
    // EnergyPortalNavigation.js, same as the Settings routes below.
    type: "hidden",
    name: "My Inbox",
    key: "energy-inbox",
    route: "/energy/inbox",
    appMode: "energy",
    component: wrap(EnergyInbox),
    noCollapse: true,
  },

  // ── Settings sub-pages ──────────────────────────────────────────────
  // Mirrored from src/lcp/routes.js — same shape, same hidden-routing,
  // same "thin wrapper around AFMS settings" pattern. The corresponding
  // sidebar entries live in src/energy/components/EnergyPortalNavigation.js.
  {
    type: "hidden",
    key: "energy-settings-organization",
    route: "/energy/settings/organization",
    appMode: "energy",
    component: wrap(EnergySettingsOrganization),
  },
  {
    type: "hidden",
    key: "energy-settings-users",
    route: "/energy/settings/users",
    appMode: "energy",
    component: wrap(EnergySettingsUsers),
  },
  {
    type: "hidden",
    key: "energy-settings-assets",
    route: "/energy/settings/assets",
    appMode: "energy",
    component: wrap(EnergySettingsAssets),
  },
  {
    type: "hidden",
    key: "energy-settings-devices",
    route: "/energy/settings/devices",
    appMode: "energy",
    component: wrap(EnergySettingsDevices),
  },
  // Hidden route (no sidebar entry) — reached from the Settings → Assets
  // listing rows. Energy-owned slim asset editor (Energy tab only).
  {
    type: "route",
    name: "Energy Asset Edit",
    key: "energy-asset-edit",
    route: "/energy/assets/edit",
    component: wrap(EnergyAssetEdit),
    appMode: "energy",
    hidden: true,
  },
  // Enterprise — hidden routes (sidebar gates visibility separately).
  {
    type: "hidden",
    key: "energy-enterprise-manage-clients",
    route: "/energy/enterprise/manage-clients",
    appMode: "energy",
    component: wrap(EnterpriseManageMyClients),
  },
  {
    type: "hidden",
    key: "energy-enterprise-manage-vendors",
    route: "/energy/enterprise/manage-vendors",
    appMode: "energy",
    component: wrap(EnterpriseManageMyVendors),
  },
];

export default energyRoutes;
